var _CloseIcon;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { CloseIcon } from '../Icons/generated';
import { projection, rubberBandIfOutOfBounds, findNearestNumberInArray, rangeMap } from './utilities';
import usePortalContainer from '../../utils/portal';
import * as Styles from './styles';
var threshold = 10;
var config = {
  tension: 439,
  friction: 40
};
var drawerHeight = 3 * 16;

var Drawer = function Drawer(_ref) {
  var removeDrawer = _ref.removeDrawer,
      children = _ref.children;
  var renderTimeRef = React.useRef(new Date());

  var _useSpring = useSpring(function () {
    return {
      y: 0,
      config: config
    };
  }),
      _useSpring2 = _slicedToArray(_useSpring, 2),
      y = _useSpring2[0].y,
      api = _useSpring2[1];

  var yStops = React.useRef([]);
  var containerRef = React.useRef(null);
  var container = usePortalContainer();
  React.useLayoutEffect(function () {
    if (!containerRef.current) return;
    yStops.current = [0, containerRef.current.clientHeight - drawerHeight];
    api.start({
      y: yStops.current[1],
      immediate: true
    });
  }, [api]);
  React.useEffect(function () {
    api.start({
      y: 0,
      immediate: false
    });
  }, [api]);

  var closeDrawer = function closeDrawer() {
    return api.start({
      y: yStops.current[1],
      onRest: removeDrawer
    });
  };

  var bind = useDrag(function (_ref2) {
    var last = _ref2.last,
        _ref2$movement = _slicedToArray(_ref2.movement, 2),
        movementY = _ref2$movement[1],
        _ref2$vxvy = _slicedToArray(_ref2.vxvy, 2),
        velocityY = _ref2$vxvy[1],
        memo = _ref2.memo;

    if (!memo) {
      var isIntentionalGesture = Math.abs(movementY) > threshold;
      if (!isIntentionalGesture) return; // @ts-ignore

      memo = y.get() - movementY;
    }

    if (!containerRef.current) return;
    if (!yStops.current.length) return;
    disableBodyScroll(containerRef.current);

    if (last) {
      enableBodyScroll(containerRef.current); // @ts-ignore

      var projectedEndpoint = y.get() + projection(velocityY);
      var point = findNearestNumberInArray(projectedEndpoint, yStops.current);
      var notificationClosed = point === yStops.current[1];
      return api.start({
        y: notificationClosed ? yStops.current[1] : yStops.current[0],
        onRest: notificationClosed ? removeDrawer : function () {},
        immediate: false
      });
    }

    var newY = rubberBandIfOutOfBounds(yStops.current[0], yStops.current[1], memo + movementY, 0.06);
    api.start({
      y: newY,
      immediate: true
    });
    return memo;
  });
  return container ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styles.Drawer, _extends({}, bind(), {
    ref: containerRef,
    as: animated.div,
    style: {
      y: y
    }
  }), /*#__PURE__*/React.createElement(Styles.CloseButton, {
    "aria-label": "close drawer",
    onClick: closeDrawer
  }, _CloseIcon || (_CloseIcon = /*#__PURE__*/React.createElement(CloseIcon, {
    "aria-hidden": true
  }))), children), /*#__PURE__*/React.createElement(Styles.Backdrop, {
    as: animated.div,
    onClick: function onClick() {
      var minMS = 100; // require 100 ms to pass after render to make it less likely to accidentally trigger close

      if (Date.now() - renderTimeRef.current.getTime() > minMS) closeDrawer();
    },
    style: {
      opacity: y.to(function (y) {
        var opacity = rangeMap(yStops.current, [0.64, 0], y);
        return isNaN(opacity) ? 0 : opacity;
      })
    }
  })), container) : null;
};

Drawer.defaultProps = {
  removeDrawer: function removeDrawer() {}
};
Drawer.propTypes = {
  // @ts-ignore
  removeDrawer: PropTypes.func.isRequired,
  // @ts-ignore
  children: PropTypes.node.isRequired
};
/** @component */

export default Drawer;